<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { convertSecondsToTimeUnit } from '@chatwoot/utils';
import { picoSearch } from '@scmmishra/pico-search';

import AddSLA from './AddSLA.vue';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import WootLabel from 'dashboard/components-next/label/Label.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';

const store = useStore();
const { t } = useI18n();

const records = useMapGetter('sla/getSLA');
const uiFlags = useMapGetter('sla/getUIFlags');
const loading = ref({});
const showAddPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedResponse = ref({});
const searchQuery = ref('');

const deleteMessage = computed(() => ` ${selectedResponse.value.name}`);

const tableHeaders = computed(() => [
  t('SLA.LIST.TABLE_HEADER.SLA'),
  t('SLA.LIST.TABLE_HEADER.BUSINESS_HOURS'),
  t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.FRT'),
  t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.NRT'),
  t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.RT'),
  t('INTEGRATION_APPS.LIST.ACTIONS'),
]);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name', 'description']);
});

const noDataMessage = computed(() => {
  if (!records.value.length) return t('SLA.LIST.404');
  if (searchQuery.value && !filteredRecords.value.length)
    return t('SLA.SEARCH.NO_RESULTS');
  return '';
});

onMounted(() => {
  store.dispatch('sla/get');
});

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedResponse.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteSla = async id => {
  try {
    await store.dispatch('sla/delete', id);
    useAlert(t('SLA.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('SLA.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedResponse.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedResponse.value.id] = true;
  closeDeletePopup();
  deleteSla(selectedResponse.value.id);
};

const displayTime = threshold => {
  const { time, unit } = convertSecondsToTimeUnit(threshold, {
    minute: 'm',
    hour: 'h',
    day: 'd',
  });
  if (!time) return '-';
  return `${time}${unit}`;
};
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('SLA.HEADER')"
    :description="$t('SLA.DESCRIPTION')"
  >
    <template #actions>
      <NextButton
        :label="$t('SLA.ADD_ACTION')"
        size="sm"
        @click="openAddPopup"
      />
    </template>

    <div class="flex items-center justify-between gap-3">
      <SettingsSearchInput
        v-model="searchQuery"
        :placeholder="$t('SLA.SEARCH_PLACEHOLDER')"
      />
      <span v-if="records.length" class="text-sm text-n-slate-11">
        {{ $t('SLA.COUNT', { n: records.length }) }}
      </span>
    </div>

    <p v-if="uiFlags.isFetching" class="text-sm text-n-slate-11">
      {{ $t('SLA.LOADING') }}
    </p>
    <BaseTable
      v-else
      :headers="tableHeaders"
      :items="filteredRecords"
      :no-data-message="noDataMessage"
    >
      <template #header-2>
        <div class="flex items-center gap-1">
          <span class="text-heading-3">
            {{ $t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.FRT') }}
          </span>
          <Icon
            v-tooltip.left="$t('SLA.LIST.RESPONSE_TYPES.FRT')"
            icon="i-lucide-info"
            class="size-3.5 text-n-slate-10 cursor-help"
          />
        </div>
      </template>
      <template #header-3>
        <div class="flex items-center gap-1">
          <span class="text-heading-3">
            {{ $t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.NRT') }}
          </span>
          <Icon
            v-tooltip.left="$t('SLA.LIST.RESPONSE_TYPES.NRT')"
            icon="i-lucide-info"
            class="size-3.5 text-n-slate-10 cursor-help"
          />
        </div>
      </template>
      <template #header-4>
        <div class="flex items-center gap-1">
          <span class="text-heading-3">
            {{ $t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.RT') }}
          </span>
          <Icon
            v-tooltip.left="$t('SLA.LIST.RESPONSE_TYPES.RT')"
            icon="i-lucide-info"
            class="size-3.5 text-n-slate-10 cursor-help"
          />
        </div>
      </template>
      <template #row="{ items }">
        <BaseTableRow v-for="sla in items" :key="sla.id" :item="sla">
          <template #default>
            <BaseTableCell>
              <div class="flex flex-col gap-1 min-w-0">
                <span class="text-body-main text-n-slate-12 truncate">
                  {{ sla.name }}
                </span>
                <span class="text-body-main text-n-slate-11 line-clamp-1">
                  {{ sla.description }}
                </span>
              </div>
            </BaseTableCell>

            <BaseTableCell class="w-40">
              <WootLabel
                :label="
                  sla.only_during_business_hours
                    ? $t('SLA.LIST.BUSINESS_HOURS_ON')
                    : $t('SLA.LIST.BUSINESS_HOURS_OFF')
                "
                :color="sla.only_during_business_hours ? 'teal' : 'slate'"
                compact
              >
                <template #icon>
                  <Icon
                    :icon="
                      sla.only_during_business_hours
                        ? 'i-lucide-alarm-clock-check'
                        : 'i-lucide-alarm-clock-off'
                    "
                    class="size-3.5"
                    :class="
                      sla.only_during_business_hours
                        ? 'text-n-teal-11'
                        : 'text-n-slate-11'
                    "
                  />
                </template>
              </WootLabel>
            </BaseTableCell>

            <BaseTableCell align="start" class="w-24">
              <span class="text-body-main text-n-slate-12">
                {{ displayTime(sla.first_response_time_threshold) }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="start" class="w-24">
              <span class="text-body-main text-n-slate-12">
                {{ displayTime(sla.next_response_time_threshold) }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="start" class="w-24">
              <span class="text-body-main text-n-slate-12">
                {{ displayTime(sla.resolution_time_threshold) }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="end" class="w-12">
              <div class="flex justify-end">
                <NextButton
                  v-tooltip.top="$t('SLA.FORM.DELETE')"
                  icon="i-woot-bin"
                  slate
                  sm
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  :is-loading="loading[sla.id]"
                  @click="openDeletePopup(sla)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddSLA @close="hideAddPopup" />
    </woot-modal>

    <ConfirmDialog
      v-model:show="showDeleteConfirmationPopup"
      :title="$t('SLA.DELETE.CONFIRM.TITLE')"
      :description="$t('SLA.DELETE.CONFIRM.MESSAGE')"
      :description-value="deleteMessage"
      :confirm-button-label="$t('SLA.DELETE.CONFIRM.YES')"
      :cancel-button-label="$t('SLA.DELETE.CONFIRM.NO')"
      @close="closeDeletePopup"
      @confirm="confirmDeletion"
    />
  </SettingsPageShell>
</template>
